import React, { useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useChatData, useChatClient } from '../context/ChatContext';
import { isValidEthAddress, shortenAddress } from '../utils/chatHelpers';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface NewGroupModalProps {
  show: boolean;
  onHide: () => void;
}

const NewGroupModal: React.FC<NewGroupModalProps> = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { createGroup, selectConversation } = useChatData();
  const { chatAddress } = useChatClient();
  const [groupName, setGroupName] = useState('');
  const [memberInput, setMemberInput] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleAddMember = useCallback(() => {
    const addr = memberInput.trim();
    if (!addr) return;

    if (!isValidEthAddress(addr)) {
      setError(t('chat.invalidAddress', 'Invalid Ethereum address'));
      return;
    }

    if (chatAddress && addr.toLowerCase() === chatAddress.toLowerCase()) {
      setError(t('chat.cannotAddSelf', 'You are already added as the group creator'));
      return;
    }

    if (members.includes(addr.toLowerCase())) {
      setError(t('chat.alreadyAdded', 'Address already added'));
      return;
    }

    setMembers((prev) => [...prev, addr.toLowerCase()]);
    setMemberInput('');
    setError('');
  }, [memberInput, members, t]);

  const handleRemoveMember = (addr: string) => {
    setMembers((prev) => prev.filter((m) => m !== addr));
  };

  const handleCreate = useCallback(async () => {
    if (members.length < 1) {
      setError(t('chat.addMembers', 'Add at least one member'));
      return;
    }

    setIsCreating(true);
    try {
      const conversationId = await createGroup(members, groupName.trim() || undefined);
      if (conversationId) {
        selectConversation(conversationId);
        setGroupName('');
        setMembers([]);
        setMemberInput('');
        onHide();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create group');
    } finally {
      setIsCreating(false);
    }
  }, [members, groupName, createGroup, selectConversation, onHide, t]);

  const handleClose = () => {
    setGroupName('');
    setMembers([]);
    setMemberInput('');
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="chat-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t('chat.newGroup', 'New Group')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="chat-modal__field">
          <label className="chat-modal__label">
            {t('chat.groupName', 'Group Name')} ({t('chat.optional', 'optional')})
          </label>
          <input
            type="text"
            className="chat-search-input"
            placeholder={t('chat.groupNamePlaceholder', 'Enter group name')}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
        </div>

        <div className="chat-modal__field">
          <label className="chat-modal__label">
            {t('chat.addMemberAddress', 'Add Member Address')}
          </label>
          <div className="chat-modal__input-row">
            <input
              type="text"
              className="chat-search-input"
              placeholder="0x..."
              value={memberInput}
              onChange={(e) => {
                setMemberInput(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleAddMember()}
            />
            <button
              className="chat-btn chat-btn--primary"
              onClick={handleAddMember}
              disabled={!memberInput.trim()}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          {error && <p className="chat-modal__error">{error}</p>}
        </div>

        {/* Member list */}
        {members.length > 0 && (
          <div className="chat-modal__members">
            <label className="chat-modal__label">
              {t('chat.members', 'Members')} ({members.length})
            </label>
            {members.map((addr) => (
              <div key={addr} className="chat-modal__member-item">
                <span className="chat-modal__member-address">{shortenAddress(addr, 8)}</span>
                <button
                  className="chat-btn chat-btn--icon"
                  onClick={() => handleRemoveMember(addr)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="chat-btn chat-btn--secondary" onClick={handleClose}>
          {t('chat.cancel', 'Cancel')}
        </button>
        <button
          className="chat-btn chat-btn--primary"
          onClick={handleCreate}
          disabled={isCreating || members.length < 1}
        >
          {isCreating ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> {t('chat.creating', 'Creating...')}
            </>
          ) : (
            t('chat.createGroup', 'Create Group')
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewGroupModal;
